import { useMemo } from 'react';
import { Form, Button, Tabs, InputNumber, Spin, message } from 'antd';
import { useEffect, useState } from 'react';
import { getPrizeList, batchUpdatePrize } from '@/api/lottoActivity';
import { useWatch } from 'antd/es/form/Form';
import { useNavigate } from 'react-router-dom';
import { PageLink } from '@/config/pageConfig';
import { isPrizePoolChanged } from '@/utils/lotto';

const StepRuleSetting = ({ form3, prev, activityId }: any) => {
  const navigate = useNavigate();
  const lottoPools = form3.getFieldValue('lottoPools') || [];
  const [loading, setLoading] = useState(false);
  const [originalPools, setOriginalPools] = useState<any[]>([]);
  useEffect(() => {
    if (!activityId) return;
    setLoading(true);
    getPrizeList(activityId)
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          const formattedPools = res.data.map((pool) => ({
            id: pool.id,
            prizePoolName: pool.prizePoolName,
            gashaponNum: pool.gashaponNum,
            prizeList: pool.prizeList.map((prize) => ({
              id: prize.id,
              prizeName: prize.prizeName,
              prizeType: prize.prizeType,
              totalStock: prize.totalStock ?? 0,
              remainStock: prize.remainStock ?? 0,
              winProbability: (prize.winProbability ?? 0) * 100,
            })),
          }));
          form3.setFieldsValue({ lottoPools: formattedPools });
          setOriginalPools(formattedPools);
        }
      })
      .finally(() => setLoading(false));
  }, [activityId]);

  const handleSave = async () => {
    try {
      await form3.validateFields();
      const formValues = form3.getFieldsValue(true); // 获取全部数据（含id）
      const changed = isPrizePoolChanged(formValues.lottoPools, originalPools);
      if (!changed) {
        message.success('库存概率配置未修改，无需保存');
        navigate(PageLink.LotteryActivity);
        return;
      }
      const not100Pools = formValues.lottoPools
        .map((pool: any, idx: number) => {
          const sum = (pool.prizeList || []).reduce(
            (acc: number, item: any) => acc + Number(item?.winProbability || 0),
            0
          );
          return sum !== 100 ? idx + 1 : null;
        })
        .filter((v: null) => v !== null);
      if (not100Pools.length > 0) {
        message.error(
          `奖池 ${not100Pools.join('、')} 的概率总和不为 100%，请检查后再保存`
        );
        return;
      }
      const payload = formValues.lottoPools.map((pool: any) => ({
        id: pool.id,
        prizePoolName: pool.prizePoolName,
        prizeList: pool.prizeList.map((prize: any) => ({
          id: prize.id,
          totalStock: prize.totalStock || 0,
          winProbability: Number(prize.winProbability || 0) / 100,
        })),
      }));
      await batchUpdatePrize(payload);
      message.success('配置保存成功');
      navigate(PageLink.LotteryActivity);
    } catch (error) {
      console.error('保存失败', error);
      message.error('保存失败，请检查填写内容');
    }
  };

  const watchedPools = useWatch('lottoPools', form3) || [];
  const remainingRates = useMemo(() => {
    return watchedPools.map((pool: { prizeList: never[] }) => {
      const prizeList = pool?.prizeList || [];
      const total = prizeList.reduce((sum: number, item: any) => {
        return sum + Number(item?.winProbability || 0);
      }, 0);
      return 100 - total;
    });
  }, [watchedPools]);

  return (
    <Spin spinning={loading}>
      <Form form={form3}>
        <div style={{ margin: '32px 0 12px', fontWeight: 600, fontSize: 16 }}>
          中奖规则<span style={{ color: '#ff4d4f' }}>（概率总和为 100%）</span>
        </div>
        <Tabs type="card">
          {lottoPools.map((pool: any, poolIdx: number) => {
            return (
              <Tabs.TabPane key={poolIdx} tab={`奖池${poolIdx + 1}`}>
                <Form.Item
                  name={['lottoPools', poolIdx, 'id']}
                  style={{ display: 'none' }}
                >
                  <InputNumber />
                </Form.Item>
                <Form.Item
                  name={['lottoPools', poolIdx, 'prizePoolName']}
                  style={{ display: 'none' }}
                >
                  <input />
                </Form.Item>
                {(() => {
                  const prizeList =
                    form3.getFieldValue(['lottoPools', poolIdx, 'prizeList']) ||
                    [];
                  return (
                    <>
                      {/* 表头 */}
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '60px auto 160px 160px 160px',
                          columnGap: 16,
                          padding: '8px 16px',
                          backgroundColor: '#f5f5f5',
                          fontWeight: 500,
                          border: '1px solid #f0f0f0',
                          textAlign: 'left',
                        }}
                      >
                        <div>序号</div>
                        <div>奖品</div>
                        <div>总库存</div>
                        <div>剩余库存</div>
                        <div>
                          概率{' '}
                          <span style={{ color: '#ff4d4f', fontSize: 12 }}>
                            （剩余 {remainingRates[poolIdx]}%）
                          </span>
                        </div>
                      </div>
                      {/* 奖品列表 */}
                      {prizeList.map((prize: any, prizeIdx: number) => (
                        <div
                          key={prizeIdx}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '60px auto 160px 160px 160px',
                            columnGap: 16,
                            padding: '12px 16px 8px',
                            borderBottom: '1px solid #f0f0f0',
                            alignItems: 'center',
                          }}
                        >
                          <Form.Item
                            name={[
                              'lottoPools',
                              poolIdx,
                              'prizeList',
                              prizeIdx,
                              'id',
                            ]}
                            style={{ display: 'none' }}
                          >
                            <InputNumber />
                          </Form.Item>
                          <div style={{ textAlign: 'left' }}>
                            {prizeIdx + 1}
                          </div>
                          <div
                            style={{
                              backgroundColor: '#f5f5f5',
                              padding: '4px 8px',
                              borderRadius: 4,
                              textAlign: 'left',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {prize.prizeName}
                          </div>

                          <div>
                            {[1, 3, 4].includes(prize.prizeType) ? (
                              <Form.Item
                                name={[
                                  'lottoPools',
                                  poolIdx,
                                  'prizeList',
                                  prizeIdx,
                                  'totalStock',
                                ]}
                                style={{ marginBottom: 0 }}
                                rules={[
                                  { required: true, message: '请输入总库存' },
                                ]}
                              >
                                <InputNumber
                                  min={0}
                                  style={{ width: '100%' }}
                                  precision={0}
                                />
                              </Form.Item>
                            ) : (
                              <div>-</div>
                            )}
                          </div>

                          <div>
                            {[1, 3, 4].includes(prize.prizeType) ? (
                              <div>
                                {form3.getFieldValue([
                                  'lottoPools',
                                  poolIdx,
                                  'prizeList',
                                  prizeIdx,
                                  'remainStock',
                                ]) ?? 0}
                              </div>
                            ) : (
                              <div>-</div>
                            )}
                          </div>

                          <div>
                            <Form.Item
                              name={[
                                'lottoPools',
                                poolIdx,
                                'prizeList',
                                prizeIdx,
                                'winProbability',
                              ]}
                              rules={[
                                { required: true, message: '请输入概率' },
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <InputNumber
                                min={0}
                                max={100}
                                addonAfter="%"
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          </div>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </Tabs.TabPane>
            );
          })}
        </Tabs>
        <div style={{ marginTop: 24 }}>
          <Button onClick={prev}>上一步</Button>
          <Button
            type="primary"
            style={{ marginLeft: 12 }}
            onClick={handleSave}
          >
            保存并退出
          </Button>
        </div>
      </Form>
    </Spin>
  );
};

export default StepRuleSetting;
